import './bootstrap';
import './safeHistory';
import '../css/app.css';

import { createApp, h } from 'vue';
import { createInertiaApp } from '@inertiajs/inertia-vue3';
import { InertiaProgress } from '@inertiajs/progress';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { ZiggyVue } from '../../vendor/tightenco/ziggy/dist/index';
import { ElNotification } from 'element-plus';
import phoneFormatterMixin from './Components/mixins/phoneFormatterMixin';
window.$notify = ElNotification;
import * as Sentry from "@sentry/vue";
import { axiosFingerprint } from './sentryFingerprint';
import { isInjectedScriptFetchFailure } from './sentryInjectedFetch';
import AppLeadCreateForm from "./Components/AppLeadCreateForm.vue";
import AppLeadView from "./Components/AppLeadView.vue"
import AppLeadSourceForm from "./Components/AppLeadSourceForm.vue";
import AppLeadSourceView from "./Components/AppLeadSourceView.vue";
import AppLeadStatusAndAssignment from "./Components/AppLeadStatusAndAssignment.vue";
import LeadSearchTable from "./Components/LeadSearchTable.vue";
import LeadSearchTableItem from "./Components/LeadSearchTableItem.vue";
import AppAgencyView from "./Components/AppAgencyView.vue";
import AppAgentCreate from "./Components/AppAgentCreate.vue";
import AppAgentsTable from "./Components/AppAgentsTable.vue";
import AppAgentView from "./Components/AppAgentView.vue";
import AppActionScheduleCreateForm from "./Components/AppActionScheduleCreateForm.vue";
import AppActionScheduleViewForm from "./Components/AppActionScheduleViewForm.vue";
import AppVoicemailScriptForm from "./Components/AppVoicemailScriptForm.vue";
import Mask from "maska";
import Swal from 'sweetalert2';
window.Swal = Swal;

import mitt from "mitt";
window.mitt = mitt();

// Froala Editor
import 'froala-editor/js/plugins.pkgd.min.js';
// Froala third party plugins
import 'froala-editor/js/plugins/image.min.js';
import 'froala-editor/js/plugins/image_manager.min.js';
// Froala Editor css files.
import 'froala-editor/css/froala_editor.pkgd.min.css';
import 'froala-editor/css/froala_style.min.css';
import VueFroala from 'vue-froala-wysiwyg';

const appName = window.document.getElementsByTagName('title')[0]?.innerText || 'Laravel';
window.visibleGlobalNotifies = [];

createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    resolve: (name) => {
        // A back/forward navigation can restore a history entry whose stored
        // Inertia page has no component name -- written by a response that
        // wasn't an Inertia page, or by a build old enough to predate the one
        // now running. Resolving './Pages/undefined.vue' throws from inside a
        // promise nothing awaits, so the agent just gets a blank screen and
        // Sentry gets an unhandled rejection (TOPBROKERCRM-2K7). A full page
        // load rebuilds the state from the server instead; the guard key stops
        // it looping if that reload somehow lands on the same broken state.
        if (!name || name === 'undefined') {
            const reloadGuardKey = 'inertia:undefined-page-reloaded';
            let alreadyReloaded = false;

            try {
                alreadyReloaded = window.sessionStorage.getItem(reloadGuardKey) === '1';
                window.sessionStorage.setItem(reloadGuardKey, '1');
            } catch (e) {
                // Private mode / blocked storage: fall through and reload once.
            }

            if (!alreadyReloaded) {
                window.location.reload();

                // Never settles, so Vue does not try to mount anything while
                // the reload is in flight.
                return new Promise(() => {});
            }

            return Promise.reject(new Error(`Inertia page could not be resolved: ${name}`));
        }

        try {
            window.sessionStorage.removeItem('inertia:undefined-page-reloaded');
        } catch (e) {
            // Nothing to clear.
        }

        return resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue'));
    },
    setup ({ el, app, props, plugin }) {
        const vm = createApp({ render: () => h(app, props) })
            .use(plugin)
            .use(ZiggyVue, Ziggy)
            .use(Mask)
            .use(VueFroala)
            .component('app-lead-create-form', AppLeadCreateForm)
            .component('app-lead-view', AppLeadView)
            .component('app-lead-source-form', AppLeadSourceForm)
            .component('app-lead-source-view', AppLeadSourceView)
            .component('app-lead-status-and-assignment', AppLeadStatusAndAssignment)
            .component('lead-search-table', LeadSearchTable)
            .component('lead-search-table-item', LeadSearchTableItem)
            .component('app-agency-view', AppAgencyView)
            .component('app-agent-create', AppAgentCreate)
            .component('app-agents-table', AppAgentsTable)
            .component('app-agent-view', AppAgentView)
            .component('app-action-schedule-create-form', AppActionScheduleCreateForm)
            .component('app-action-schedule-view-form', AppActionScheduleViewForm)
            .component('app-voicemail-script-form', AppVoicemailScriptForm)
            .mixin(phoneFormatterMixin)
            .mount(el);

        if (process.env.NODE_ENV === 'production') {
            Sentry.init({
                vm,
                dsn: "https://40f28c412d0f4831b01a98180e07b463@o252856.ingest.sentry.io/4505171365986304",
                integrations: [
                    new Sentry.Replay({
                        maskAllInputs: false,
                        maskAllText: false
                    }),
                ],
                replaysOnErrorSampleRate: 1.0,
                ignoreErrors: [
                    'Network Error',
                    'timeout of',
                    'Request aborted',
                    /^timeout exceeded$/i,
                    'ResizeObserver loop',
                    'Non-Error promise rejection captured',
                    // @sentry/browser 7.x renamed the entry above to this wording; keep
                    // both so cached older bundles still match (TOPBROKERCRM-21B).
                    'Object captured as promise rejection',
                    "Can't find variable: gmo",
                    'Failed to connect to MetaMask',
                    'Invalid call to runtime.sendMessage',
                    'Could not load "util"',
                    /Object Not Found Matching Id:/,
                    'Failed to fetch dynamically imported module',
                    // Same stale-chunk family as the entry above: Vite throws this when the CSS of a
                    // lazily-loaded page 404s after a deploy replaced its hashed chunk (TOPBROKERCRM-2HD).
                    'Unable to preload CSS',
                    /Blocked a frame with origin/,
                    'WKWebView API client did not respond to this postMessage',
                    // Same native-bridge family as the entry above: the browser app's own injected
                    // script posts to a window.webkit.messageHandlers handler that is gone. The app
                    // never calls one; no stack, DuckDuckGo on iOS (TOPBROKERCRM-2MK).
                    'Script message handler is unavailable',
                    /Failed to execute 'transaction' on 'IDBDatabase'/,
                    'The database connection is closing',
                ],
                denyUrls: [
                    /^chrome-extension:\/\//,
                    /^moz-extension:\/\//,
                    /^safari-(web-)?extension:\/\//,
                    /^webkit-masked-url:/,
                    /^chrome:\/\//,
                    /\/extensions\//,
                    /maps\.googleapis\.com\/maps\/api\/js/,
                    /\/froala-editor\//,
                    /\/leaflet\//,
                    /\/vendor-froala-/,
                    /\/vendor-leaflet-/,
                    /\/vendor-selectable-/,
                    /\/frame-modern\./,
                    /\.safariextension\//,
                    /FormMetadata\.js/,
                ],
                beforeSend(event, hint) {
                    // A fetch() that failed inside a script the visitor's browser
                    // injected into the page (TOPBROKERCRM-2M5/2M6/2M7/2M8). See
                    // resources/js/sentryInjectedFetch.js.
                    if (isInjectedScriptFetchFailure(event, window.location.origin)) {
                        return null;
                    }

                    const error = hint?.originalException;
                    if (error && error.message === 'Network Error' && !error.response) {
                        return null;
                    }
                    if (error?.code === 'ECONNABORTED' || error?.code === 'ERR_CANCELED') {
                        return null;
                    }
                    const status = error?.response?.status;
                    if (status === 401 || status === 419) {
                        return null;
                    }

                    // Group the remaining axios HTTP failures by the request
                    // that failed rather than by the minified frame that threw
                    // (Y8/G8/CT/settle), which is a different name after every
                    // build. See resources/js/sentryFingerprint.js.
                    const grouping = axiosFingerprint(error);
                    if (grouping) {
                        event.fingerprint = grouping.fingerprint;
                        event.tags = { ...event.tags, ...grouping.tags };
                    }

                    return event;
                },
            });
        }

        return vm;

    },
});

InertiaProgress.init({ color: '#4B5563' });
